import { useState, useEffect } from 'react';
import { useNavigate, Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { format, addMonths } from 'date-fns';
import Async from 'react-select/async';
import DatePicker from 'react-datepicker';
import { CalendarContainer } from 'react-datepicker';
import DatePickerWrapper from '../../components/Datepicker/DatepickerWrapper';

import { API } from '../../API';
import { showRequestError } from '../../utils/requestError';
import './ReportEditPage.scss';
import '../SearchPage/Calendar/ReactDatepicker.css';

export function ReportEditPage() {
  const { t } = useTranslation();
  let navigate = useNavigate();
  let { reportId } = useParams();

  const [values, setValues] = useState({
    name: '',
  });
  const [narratives, setNarratives] = useState([]);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [errors, setErrors] = useState({});

  function removeTimezoneAndSetMidnight(dateString) {
    const date = format(dateString, 'yyyy-LL-dd') + ' 00:00:00'
    return date;
  }

  useEffect(() => {
    API.fetch('GET', `/API/v1/reports/${reportId}`).then((data) => {
      setValues({ name: data.name });
      if (data.parameters.narrative_ids) {
        const promises = data.parameters.narrative_ids.map((narrative_id) =>
          API.fetch('GET', `/API/v1/narratives/${narrative_id}`),
        );

        Promise.allSettled(promises)
          .then((results) => {
            let narratives = results.map((v) => {
              if (v.status === 'fulfilled') {
                return { value: v.value.id, label: v.value.name };
              } else {
                return null;
              }
            });
            setNarratives(narratives.filter((v) => v !== null));
          })
          .catch((error) => showRequestError(error, t('Narratives')));
      }
      if (data.parameters.start_date) {
        setStartDate(new Date(data.parameters.start_date));
      }
      if (data.parameters.end_date) {
        setEndDate(new Date(data.parameters.end_date));
      }
    }).catch((error) => showRequestError(error, t('Report')));
  }, [reportId]);

  const handleChange = (e) => {
    setValues((oldValues) => ({
      ...oldValues,
      [e.target.name]: e.target.value,
    }));
  };

  const promiseOptions = (inputValue) =>
    new Promise((resolve) => {
      API.fetch('GET', `/API/v1/narratives?q=${inputValue}`).then((data) => {
        resolve(
          data.objects.map((narrative) => {
            return { value: narrative.id, label: narrative.name };
          }),
        );
      }).catch((error) => {
        showRequestError(error, t('Narratives'));
        resolve([]);
      });
    });

  const handleSubmit = (e) => {
    e.preventDefault();

    if(narratives.length === 0) {
      setErrors({ ...errors, cases: t('Please select at least one case') });
      return;
    }

    API.fetch('PUT', `/API/v1/reports/${reportId}`, null, {
      name: values.name,
      parameters: {
        narrative_ids: narratives.map((v) => v.value),
        start_date: startDate ? removeTimezoneAndSetMidnight(startDate) : null,
        end_date: endDate ? removeTimezoneAndSetMidnight(endDate) : null,
      },
    }).then((data) => {
      navigate(`/reports/${reportId}`);
    }).catch((error) => showRequestError(error, t('Edit report')));
  };

  const container = ({ children }) => {
    return (
      <div className='react-datepicker custom-datepicker'>
        <CalendarContainer>
          <div style={{ position: "relative" }}>{children}</div>
        </CalendarContainer>
      </div>
    )
  }

  return (
    <div>
      <div className="page-header">
        <div className="breadcrumb">
          <span>
            <Link to="/reports">{t('Reports')}</Link>
          </span>
          <span>
            <Link to={`/reports/${reportId}`}>{values.name}</Link>
          </span>
          <span>{t('New')}</span>
        </div>
      </div>
      <div className="form-wrapper" translate="no">
        <form method="post" onSubmit={handleSubmit}>
          <div className="form-element">
            <label htmlFor="name">{t('Name')}</label>
            <input
              className=""
              id="name"
              name="name"
              value={values.name}
              type="text"
              onChange={handleChange}
              required
            />
          </div>

          <div
            className={errors.cases ? 'form-element error' : 'form-element'}
          >
            <label>{t('Narratives')}</label>
            <Async
              className="react-select-container"
              classNamePrefix="react-select"
              cacheOptions
              defaultOptions
              isMulti={true}
              loadOptions={promiseOptions}
              placeholder={t('Select')}
              name="narratives"
              value={narratives}
              onChange={(values) => setNarratives(values)}
            />
            <div className="error-message">{errors.cases}</div>
          </div>
          <div className="form-element">
            <label>{t('Date range')}</label>
            <DatePickerWrapper
              onChange={(startDate, endDate) => {
                setStartDate(startDate);
                setEndDate(endDate);
              }}
              initStartDate={startDate}
              initEndDate={endDate}
            />
          </div>

          <div className="form-element">
            <div className="submit-wrapper">
              <input type="submit" value={t('Save')} />
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
